import {useMemo} from 'react';
import {useAppSelector} from '@/hooks';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import {SelectionType} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';

type ItemUid = string;

export function useGetFabricObjectPropertiesMap<T extends keyof FabricObject>(
  pageHashedId: string,
  keys: T[],
  selectionType: SelectionType
): Record<ItemUid, Pick<FabricObject, T>> | undefined {
  const hoveredItemId = useAppSelector((state) => state.posterEditor.hoveredItemId[pageHashedId]);
  const activeItemIds = useAppSelector((state) => state.posterEditor.activeItemIds[pageHashedId]);

  // need to keep this to always be listening to the itemsHashMap, so that if any of the required keys change, we then propogate that up to the
  // react component (which listens only to specific keys to prevent re-renders)
  const itemsHashMap = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.pages.pagesHashMap[pageHashedId]?.items?.itemsHashMap;
  });

  return useMemo(() => {
    if (!itemsHashMap) {
      return undefined;
    }

    const whiteboard = window.posterEditor?.whiteboard;

    if (selectionType === SelectionType.HOVERED_ITEM && hoveredItemId) {
      const hoveredItem = whiteboard?.getItemForId(hoveredItemId);
      if (!hoveredItem) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = hoveredItem.fabricObject.get(key) as FabricObject[T];
      }

      return {[hoveredItem.uid]: obj as Pick<FabricObject, T>};
    }

    if (selectionType === SelectionType.MULTI_SELECTION) {
      const page = whiteboard?.pages.getPage(pageHashedId);
      const activeSelection = page?.activeSelection.getActiveObject();

      if (!(activeSelection instanceof ActiveSelection)) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = activeSelection.get(key) as FabricObject[T];
      }

      return {'multi-selection': obj as Pick<FabricObject, T>};
    }

    if (!activeItemIds || activeItemIds.length === 0) {
      return undefined;
    }

    const result: Record<ItemUid, Pick<FabricObject, T>> = {};
    for (const itemId of activeItemIds) {
      const item = whiteboard?.getItemForId(itemId);
      if (!item) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = item.fabricObject.get(key) as FabricObject[T];
      }

      result[item.uid] = obj as Pick<FabricObject, T>;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }, [hoveredItemId, activeItemIds, itemsHashMap, pageHashedId, keys, selectionType]);
}
